import { CSSProperties, PropsWithChildren, ReactNode, useCallback, useRef, useState } from 'react';

import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  ReferenceType,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  UseFloatingOptions,
  useFocus,
  useHover,
  useInteractions,
  useListNavigation,
  useTransitionStyles,
} from '@floating-ui/react';
import classNames from 'classnames';

import styles from './index.module.scss';

export type UseFloatingContainerOptions<T extends ReferenceType = ReferenceType> = Pick<UseFloatingOptions<T>, 'open' | 'placement'> & {
  setMinWidth?: boolean;
  setMaxWidth?: boolean;
  enableFocus?: boolean;
  enableHover?: boolean;
  enableArrow?: boolean;
  enableListNavigation?: boolean;
};

type FloatingContainerProps = PropsWithChildren<{
  className?: string;
}>;

const ARROW_HEIGHT = 7;
const GAP = 4;

export default function useFloatingContainer<T extends ReferenceType = ReferenceType>({
  open,
  placement = 'bottom-start',
  setMinWidth,
  setMaxWidth,
  enableFocus = false,
  enableHover = false,
  enableArrow = false,
  enableListNavigation = false,
}: UseFloatingContainerOptions<T> = {}) {
  const [isOpen, setOpen] = useState(open);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const floatingStylesRef = useRef<CSSProperties>({});
  const listRef = useRef<HTMLElement[]>([]);
  const arrowRef = useRef(null);
  const arrowComponentRef = useRef<ReactNode>(null);

  const { refs, floatingStyles, context } = useFloating<T>({
    open: isOpen,
    onOpenChange: setOpen,
    placement,
    middleware: [
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5,
      }),
      shift({ padding: 5 }),
      arrow({
        element: arrowRef,
      }),
      offset(enableArrow ? ARROW_HEIGHT + GAP : 0),
    ],
    whileElementsMounted: autoUpdate,
  });

  const focus = useFocus(context, { enabled: enableFocus, visibleOnly: false });
  const hover = useHover(context, { enabled: enableHover, handleClose: safePolygon() });
  const dismiss = useDismiss(context);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: {
      open: 200,
      close: 100,
    },
  });

  const listNavigation = useListNavigation(context, {
    enabled: enableListNavigation,
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: true,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([dismiss, focus, hover, listNavigation]);

  const triggerElementWidth = (refs.reference?.current as HTMLElement)?.getBoundingClientRect().width || 0;

  floatingStylesRef.current = {
    ...floatingStyles,
    ...transitionStyles,
    minWidth: setMinWidth ? triggerElementWidth : undefined,
    maxWidth: setMaxWidth ? triggerElementWidth : undefined,
    zIndex: 1000,
  };

  if (enableArrow) {
    arrowComponentRef.current = <FloatingArrow context={context} height={ARROW_HEIGHT} ref={arrowRef} />;
  }

  const FloatingContainer = useCallback(
    ({ children, className }: FloatingContainerProps) => (
      <FloatingPortal>
        {isMounted ? (
          <FloatingFocusManager context={context} initialFocus={-1}>
            <div
              className={classNames(styles.container, className)}
              ref={refs.setFloating}
              style={floatingStylesRef.current}
              {...getFloatingProps()}
            >
              {children}
              {arrowComponentRef.current}
            </div>
          </FloatingFocusManager>
        ) : null}
      </FloatingPortal>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refs.setFloating, isMounted],
  );

  const _getReferenceProps = (enterCallback?: (index: number) => void) =>
    getReferenceProps({
      ref: refs.setReference,
      onKeyDown(e) {
        if (e.key === 'Enter' && activeIndex !== null) {
          e.preventDefault();
          enterCallback?.(activeIndex);
          setActiveIndex(null);
          setOpen(false);
        }
      },
    });

  const _getItemProps = (index: number) => {
    const isActive = activeIndex === index;

    return getItemProps({
      tabIndex: isActive ? 0 : -1,
      ref: (node: HTMLElement) => {
        listRef.current[index] = node;
      },
      'data-active': isActive,
    } as Parameters<typeof getItemProps>[0]);
  };

  return {
    isOpen,
    setOpen,
    triggerRef: refs.domReference,
    getReferenceProps: _getReferenceProps,
    getItemProps: _getItemProps,
    FloatingContainer,
  };
}
